"use client";

/**
 * useSetupSession Hook
 *
 * Manages session tracking for the setup flow. Initializes an anonymous session
 * on mount, tracks step progression and username submissions, and links the
 * session to a user account after signup.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import type { Platform } from "../types";
import { getAnalyticsId, getLandingUrl } from "@/lib/analytics/posthog-client";
interface UseSetupSessionReturn {
  /** Unique session ID from the server */
  sessionId: string | null;
  /** True once the session has been initialized (or failed to initialize) */
  isInitialized: boolean;
  /** Track when user enters a new step */
  trackStep: (step: number) => Promise<void>;
  /** Track when user submits a username */
  trackUsername: (username: string, platform: Platform) => Promise<void>;
  /** Track when user reaches the payment (card form) sub-page. Fires once per session. */
  trackPaymentFormEnter: () => Promise<void>;
  /** Track when user presses the pay/submit button. Fire-and-forget, first press wins. */
  trackPaymentSubmit: () => Promise<void>;
  /** Link anonymous session to authenticated user after signup */
  linkToUser: () => Promise<void>;
}
export function useSetupSession(): UseSetupSessionReturn {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const hasInitializedRef = useRef(false);

  // Promise that resolves when session is initialized (prevents race condition)
  const initPromiseRef = useRef<Promise<boolean> | null>(null);

  // Initialize session on mount
  useEffect(() => {
    // Prevent double initialization in strict mode
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    const initSession = async (): Promise<boolean> => {
      try {
        // Include current query params (first-hit attribution robustness)
        const query = window.location.search || "";
        const res = await fetch(`/api/setup/session${query}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          credentials: "include",
          // Critical for cookies
          // Analytics visitor id + the session's landing URL (msclkid, rdt_cid, creative, keyword...)
          body: JSON.stringify({
            ph_distinct_id: getAnalyticsId(),
            landing_url: getLandingUrl()
          })
        });
        if (!res.ok) {
          throw new Error(`Session init failed: ${res.status}`);
        }
        const data = await res.json();
        setSessionId(data.sessionId || data.session?.session_token || null);
        setIsInitialized(true);
        return true; // Session created successfully
      } catch (error) {
        console.error("[SetupSession] Init failed:", error);
        // Still mark as initialized to not block the flow
        setIsInitialized(true);
        return false; // Session failed to create
      }
    };
    initPromiseRef.current = initSession();
  }, []);

  // Track step changes - waits for session initialization before sending
  const trackStep = useCallback(async (step: number) => {
    // Wait for session initialization to complete (prevents 404 race condition)
    const initialized = await initPromiseRef.current;
    if (!initialized) return; // Don't send PATCH if session creation failed

    try {
      await fetch("/api/setup/session", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          step,
          action: "step_enter",
          ph_distinct_id: getAnalyticsId()
        })
      });
    } catch (error_0) {
      console.error("[SetupSession] Track step failed:", error_0);
      // Silent fail - don't block user experience
    }
  }, []);

  // Track username submission - waits for session initialization before sending
  const trackUsername = useCallback(async (username: string, platform: Platform) => {
    // Wait for session initialization to complete (prevents 404 race condition)
    const initialized_0 = await initPromiseRef.current;
    if (!initialized_0) return; // Don't send PATCH if session creation failed

    try {
      await fetch("/api/setup/session", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          username,
          platform,
          action: "username_submit",
          ph_distinct_id: getAnalyticsId()
        })
      });
    } catch (error_1) {
      console.error("[SetupSession] Track username failed:", error_1);
      // Silent fail - don't block user experience
    }
  }, []);

  // Track payment (card form) sub-page entry — fires once per session (guards
  // against re-fires when the user navigates back to plan selection and forward again)
  const hasTrackedPaymentFormRef = useRef(false);
  const trackPaymentFormEnter = useCallback(async () => {
    if (hasTrackedPaymentFormRef.current) return;
    hasTrackedPaymentFormRef.current = true;
    const initialized_1 = await initPromiseRef.current;
    if (!initialized_1) return;
    try {
      await fetch("/api/setup/session", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        keepalive: true,
        body: JSON.stringify({
          action: "payment_form_enter",
          ph_distinct_id: getAnalyticsId()
        })
      });
    } catch (error_2) {
      console.error("[SetupSession] Track payment form failed:", error_2);
      // Silent fail - don't block user experience
    }
  }, []);

  // Track pay/submit button press — fires once per session (first press wins).
  // Fire-and-forget beacon sent BEFORE the charge request; must never block submission.
  const hasTrackedPaymentSubmitRef = useRef(false);
  const trackPaymentSubmit = useCallback(async () => {
    if (hasTrackedPaymentSubmitRef.current) return;
    hasTrackedPaymentSubmitRef.current = true;
    const initialized_2 = await initPromiseRef.current;
    if (!initialized_2) return;
    try {
      await fetch("/api/setup/session", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        keepalive: true,
        body: JSON.stringify({
          action: "payment_submit",
          ph_distinct_id: getAnalyticsId()
        })
      });
    } catch (error_3) {
      console.error("[SetupSession] Track payment submit failed:", error_3);
      // Silent fail - don't block user experience
    }
  }, []);

  // Link session to user after signup
  const linkToUser = useCallback(async () => {
    try {
      // Ensure session cookie exists before linking
      const initialized_3 = await initPromiseRef.current;
      if (!initialized_3) return;
      await fetch("/api/setup/session/link", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          ph_distinct_id: getAnalyticsId()
        })
      });
    } catch (error_4) {
      console.error("[SetupSession] Link failed:", error_4);
      // Silent fail - not critical if this fails
    }
  }, []);
  return {
    sessionId,
    isInitialized,
    trackStep,
    trackUsername,
    trackPaymentFormEnter,
    trackPaymentSubmit,
    linkToUser
  };
}